import { CloseOutlined, ExpandAltOutlined, FullscreenExitOutlined, FullscreenOutlined, QuestionCircleOutlined, UndoOutlined } from "@ant-design/icons";
import { XbotPageContext } from "@dto/pyxbot-agent.dto";
import { Avatar, Button, Popover, Tooltip } from "antd";
import { ButtonHTMLAttributes, FC, Ref, useCallback, useEffect, useEffectEvent, useLayoutEffect, useRef, useState } from "react";
import { LuBotMessageSquare } from "react-icons/lu";
import { Label } from "src/data/Label";
import { AIChat } from "../AIChat";
import { currentPageContext } from "../pageContext";
import {
  ChatWindowSize, coversPage, DEFAULT_CHAT_SIZE, fitChatSize, forgetChatSize, keepChatSize, overlayOpen, readChatSize,
} from "./chatWindowSize";
import { HELP_URL } from "./starterQuestions";
import { XbotHint } from "./XbotHint";

/**
 * The round chat icon in the corner: a real button, so it is reached with Tab and named to a screen reader. The
 * Popover around it adds its own class while open (ant-popover-open), which joins the icon's rather than replacing it.
 */
export const ChatIconButton: FC<ButtonHTMLAttributes<HTMLButtonElement> & { ref?: Ref<HTMLButtonElement> }> = ({ className, ...button }) =>
  <button type="button" className={className ? `xm-chat-icon ${className}` : "xm-chat-icon"} {...button}>
    <Avatar size={44} icon={<LuBotMessageSquare />} aria-hidden />
  </button>;

/** Which way a drag resizes: the window is anchored bottom right, so its left edge sets the width and its top edge the height. */
type ResizeAxis = 'width' | 'height' | 'both';

function screenSize(): ChatWindowSize {
  return { width: window.innerWidth, height: window.innerHeight };
}

const AIChatPopover: FC = () => {
  const [open, setOpen] = useState(false);
  const [enlarged, setEnlarged] = useState(false);
  // Captured once at open time; navigating away afterwards must not change what was asked about.
  const [pageContext, setPageContext] = useState<XbotPageContext>();

  const iconRef = useRef<HTMLButtonElement>(null);

  /** Closed by Escape or its own button: the keyboard goes back to the icon it was opened from. */
  const closeChat = () => {
    setOpen(false);
    iconRef.current?.focus();
  };

  // The size left last time, fitted to this screen; the default when none was kept.
  const [size, setSize] = useState<ChatWindowSize>(() => {
    const kept = readChatSize();
    return fitChatSize(kept ?? DEFAULT_CHAT_SIZE, screenSize());
  });
  const [hasMoved, setHasMoved] = useState(() => readChatSize() !== null);
  /** The size the window had before it stepped aside for a page opened from it; null while it has not. */
  const [aside, setAside] = useState<ChatWindowSize | null>(null);
  // The window was enlarged when it stepped aside, so going back enlarges it again.
  const [asideFromEnlarged, setAsideFromEnlarged] = useState(false);

  const frame = useRef<number | null>(null);
  /** The size the newest drag move asked for, still to be drawn in the next frame. */
  const dragged = useRef<ChatWindowSize | null>(null);
  const resizing = useRef<ResizeAxis | null>(null);
  const startPos = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const onResizeStart = (e: React.MouseEvent, axis: ResizeAxis) => {
    e.preventDefault();
    // a size the person drags is theirs: a window that stepped aside no longer goes back, nor enlarges again
    setAside(null);
    setAsideFromEnlarged(false);
    e.stopPropagation();
    resizing.current = axis;

    const el = popoverRef.current;
    if (!el) return;

    startPos.current = {
      x: e.clientX,
      y: e.clientY,
      width: el.offsetWidth,
      height: el.offsetHeight,
    };
  };

  const onResizing = (e: MouseEvent) => {
    if (!resizing.current) return;

    const dx = e.clientX - startPos.current.x;
    const dy = e.clientY - startPos.current.y;

    const axis = resizing.current;
    // never past the screen's edge, where the grab bars could not be reached again
    const { width: newWidth, height: newHeight } = fitChatSize({
      width: axis === 'height' ? startPos.current.width : startPos.current.width - dx,
      height: axis === 'width' ? startPos.current.height : startPos.current.height - dy,
    }, screenSize());

    if (frame.current) cancelAnimationFrame(frame.current);

    dragged.current = { width: newWidth, height: newHeight };
    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      dragged.current = null;
      const el = popoverRef.current;
      if (!el) return;
      el.style.width = `${newWidth}px`;
      el.style.height = `${newHeight}px`;
      setHasMoved(true);
    });
  };

  const onResizeEnd = () => {
    if (!resizing.current) return;
    resizing.current = null;
    if (frame.current) cancelAnimationFrame(frame.current);
    frame.current = null;
    const el = popoverRef.current;
    // the last move may still wait for its frame: it is drawn now, or the mouse would end where the window does not
    const last = dragged.current;
    dragged.current = null;
    if (el && last) {
      el.style.width = `${last.width}px`;
      el.style.height = `${last.height}px`;
      setHasMoved(true);
    }
    // The size the drag left is kept for the next opening, also after a reload.
    if (el) {
      const left = { width: el.offsetWidth, height: el.offsetHeight };
      keepChatSize(left);
      setSize(left);
    }
  };

  // Stable, and a size that has not changed is not set again: the browser sends resize events in bursts, and every
  // new size object would draw the whole conversation afresh.
  const drawSize = useCallback((next: ChatWindowSize) => {
    const el = popoverRef.current;
    if (el) {
      el.style.width = `${next.width}px`;
      el.style.height = `${next.height}px`;
    }
    setSize((was) => (was.width === next.width && was.height === next.height ? was : next));
  }, []);

  const resetSizeToDefault = () => {
    setAside(null);
    forgetChatSize();
    drawSize(fitChatSize(DEFAULT_CHAT_SIZE, screenSize()));
    setHasMoved(false)
  }

  // A smaller browser window shrinks the chat to fit; a larger one gives back the size it was given, never more.
  const onScreenResize = useEffectEvent(() => {
    if (resizing.current) {
      return;
    }
    const wanted = aside ? DEFAULT_CHAT_SIZE : readChatSize() ?? DEFAULT_CHAT_SIZE;
    drawSize(fitChatSize(wanted, screenSize()));
  });

  useEffect(() => {
    const onScreen = () => onScreenResize();
    window.addEventListener('mousemove', onResizing);
    window.addEventListener('mouseup', onResizeEnd);
    window.addEventListener('resize', onScreen);
    return () => {
      window.removeEventListener('mousemove', onResizing);
      window.removeEventListener('mouseup', onResizeEnd);
      window.removeEventListener('resize', onScreen);
    };
  }, []);

  // Read when a page opens, not when the chat draws: so onPageOpened stays one function, and a new size does not
  // hand the chat (ChatContext, AIChat.tsx) a new value that would draw the whole conversation again.
  const shown = useRef({ size, enlarged });
  useLayoutEffect(() => {
    shown.current = { size, enlarged };
  }, [size, enlarged]);

  // A page opened from the chat opens under it: a window that covers the page steps aside to its default
  // size in the corner, so the page is seen; the conversation stays, and a button in the head takes it back.
  const onPageOpened = useCallback(() => {
    const { size: was, enlarged: wasEnlarged } = shown.current;
    if (!coversPage(was, screenSize(), wasEnlarged)) {
      return;
    }
    setAsideFromEnlarged(wasEnlarged);
    setEnlarged(false);
    setAside(was);
    drawSize(fitChatSize(DEFAULT_CHAT_SIZE, screenSize()));
  }, [drawSize]);

  const backToLarge = () => {
    if (aside) {
      drawSize(fitChatSize(aside, screenSize()));
    }
    setAside(null);
    setEnlarged(asideFromEnlarged);
    setAsideFromEnlarged(false);
  };

  // Escape closes the uppermost thing only: a window or a picture opened over the chat (History, the
  // disclaimer, an enlarged answer or manual picture) closes on its own, and the chat stays
  const onEscape = useEffectEvent((e: KeyboardEvent) => {
    if (e.key === 'Escape' && !e.defaultPrevented && !overlayOpen()) {
      closeChat();
    }
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (e: KeyboardEvent) => onEscape(e);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const switchEnlarged = () => {
    // the person's own choice of size: a window that stepped aside no longer goes back
    setAside(null);
    setAsideFromEnlarged(false);
    setEnlarged(prev => !prev);
  }

  const stepsBack = aside !== null || asideFromEnlarged;

  const openChat = () => {
    setPageContext(currentPageContext());
    setOpen(true);
  };

  return <><Popover classNames={{ root: `xm-chat-popover ${enlarged ? 'xm-chat-popover-enlarged' : ''}` }}
    styles={{
      root: {
        transition: 'none',
      },
    }}
    content={<div className="ai-chat-wrapper" ref={popoverRef} style={{
      width: size.width,
      height: size.height
    }}>
      {/* Grab bars on the edges that move: the window is anchored at the icon, bottom right. */}
      {!enlarged && <>
        <div className="xm-chat-resize xm-chat-resize-left" onMouseDown={(e) => onResizeStart(e, 'width')} aria-hidden />
        <div className="xm-chat-resize xm-chat-resize-top" onMouseDown={(e) => onResizeStart(e, 'height')} aria-hidden />
        <div className="xm-chat-resize xm-chat-resize-corner" onMouseDown={(e) => onResizeStart(e, 'both')} aria-hidden />
      </>}
      <AIChat open={open} pageContext={pageContext} onPageOpened={onPageOpened} />
    </div>} open={open}
    title={<div className="xm-chat-header">
      <div className="title">
        <span className="flex">{Label.XORBOT}</span>
      </div>
      <div>
        {/* Opens docs in a new tab; the chat stays open with whatever was typed. */}
        <Tooltip title="Help">
          <Button type="text" size="small" aria-label="Help" href={HELP_URL} target="_blank" rel="noopener noreferrer" icon={<QuestionCircleOutlined />} />
        </Tooltip>
        {stepsBack && <Button type="text" size="small" aria-label="Back to the large window" onClick={backToLarge}>
          <ExpandAltOutlined title="Back to the large window" />
        </Button>}
        {hasMoved && !enlarged && !stepsBack && <Button type="text" size="small" aria-label="Restore default size" onClick={resetSizeToDefault}>
          <UndoOutlined title="Restore default size" />
        </Button>}
        <Button type="text" size="small" aria-label={enlarged ? "Restore size" : "Enlarge window"} onClick={switchEnlarged}>
          {enlarged ? <FullscreenExitOutlined title="Restore size" /> : <FullscreenOutlined title="Enlarge window" />}
        </Button>
        <Button type="text" size="small" aria-label="Close chat" onClick={closeChat}><CloseOutlined title="Close chat" /></Button>
      </div>
    </div>}
    // no trigger: antd's own outside-click close would also fire for browser-extension clicks and discard a half-typed draft
    trigger={[]} placement="topRight">
    <ChatIconButton ref={iconRef} aria-label={`Open ${Label.XORBOT}`} aria-expanded={open} onClick={() => {
      if (open) {
        setOpen(false);
      } else {
        openChat();
      }
    }} />
  </Popover>
  <XbotHint chatOpen={open} onOpenChat={openChat} />
  </>;
}

export default AIChatPopover;
